import { sendHelpdeskEmail } from '@/hooks/useHelpDesk'
import {
  Box,
  Button,
  CloseButton,
  Flex,
  FormControl,
  Heading,
  IconButton,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react'
import { useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { useContext, useEffect } from 'react'
import * as yup from 'yup'

import { Card, CardBody } from '../components/Card'
import { FormLabel } from '../components/FormLabel'
import { InputGroup } from '../components/InputGroup'
import { TextareaGroup } from '../components/TextareaGroup'
import { useAuth } from '../context/AuthContext'
import { useApp } from '../hooks/useApp'
import { Trash, Upload } from '../icons'
import { insuranceParamsContext } from '../layouts/Wizard'

interface HelpDeskFormValues {
  name: string
  email: string
  phone: string
  subject: string
  questionType: string
  message: string
  files?: File[] | null
}

const schema = yup
  .object()
  .shape({
    name: yup.string().required('Zadejte jméno a příjmení'),
    email: yup.string().required('Zadejte email'),
    subject: yup.string().required('Zadejte předmět'),
    message: yup.string().required('Napište zprávu'),
  })
  .required()

export const HelpDesk = ({
  isOpen,
  onOpen,
  onClose,
}: {
  isOpen: boolean
  onOpen: Function
  onClose: Function
}) => {
  const { user } = useAuth() ?? {}

  const { product } = useApp()

  const { insuranceParamsId } = useContext(insuranceParamsContext)

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<HelpDeskFormValues>({
    mode: 'onBlur',
    resolver: yupResolver(schema) as any,
    defaultValues: {
      name: user?.userFullName,
      email: user?.email,
      phone: user?.phoneNumber,
    },
  })

  useEffect(() => {
    reset({
      name: user?.userFullName,
      email: user?.email,
      phone: user?.phoneNumber,
      subject: insuranceParamsId ? 'Nabídka ' + insuranceParamsId : '',
    })
  }, [user, insuranceParamsId, reset])

  const toast = useToast()
  const files = watch('files') || []
  const filesArray = files

  const onSubmit = (values: HelpDeskFormValues) =>
    sendHelpdeskEmail({
      name: values.name,
      email: values.email,
      phone: values.phone,
      subject: values.subject,
      message: values.message,
      product: product ?? undefined,
      files: values.files,
    })
      .then((resp) => {
        toast({
          title: 'Zpráva odeslána!',
          description: 'Vaše zpráva HelpDesku byla úspěšně odeslána.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        })
        reset() // reset form
        onClose() // close helpdesk
      })
      .catch(() => {
        toast({
          title: 'Zprávu se nepodařilo odeslat!',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      })

  return (
    <Card
      sx={{
        display: 'grid',
        position: 'fixed',
        top: 'auto',
        height: '100%',
        maxH: 'calc(100vh - 12rem)',
        right: 4,
        bottom: { base: 40, xl: 24 },
        transform: isOpen ? 'translateX(0)' : 'translateX(200%)',
        transitionProperty: 'all',
        transitionDuration: 'fast',
        boxShadow: '3xl',
        overflow: 'hidden',
        zIndex: 1200,
      }}
    >
      <CardBody sx={{ height: '100%', overflow: 'auto' }}>
        <Flex
          as="header"
          sx={{ mb: 4, alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Heading as="h2" sx={{ fontSize: 'lg' }}>
            HelpDesk
          </Heading>

          <CloseButton onClick={() => onClose()} />
        </Flex>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ display: 'grid', gap: 4 }}>
            <FormControl isInvalid={!!errors.name}>
              <FormLabel htmlFor="name">Jméno a příjmení</FormLabel>

              <InputGroup
                type="text"
                inputMode="text"
                autoComplete="name"
                placeholder="Vaše jméno a příjmení"
                errors={errors.name}
                {...register('name')}
              />
            </FormControl>

            <FormControl isInvalid={!!errors.email}>
              <FormLabel htmlFor="email">E-mail</FormLabel>

              <InputGroup
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="Váš e-mail"
                errors={errors.email}
                {...register('email')}
              />
            </FormControl>

            <FormControl>
              <FormLabel htmlFor="phone">Telefon</FormLabel>

              <InputGroup
                type="tel"
                inputMode="tel"
                autoComplete="phone"
                placeholder="Vaše telefonní číslo"
                {...register('phone')}
              />
            </FormControl>

            <FormControl isInvalid={!!errors.subject}>
              <FormLabel htmlFor="subject">Předmět</FormLabel>

              <InputGroup
                type="text"
                inputMode="text"
                autoComplete="off"
                placeholder="Např. Číslo nabídky"
                errors={errors.subject}
                {...register('subject')}
              />
            </FormControl>

            <FormControl variant="fullWidth" isInvalid={!!errors.message}>
              <FormLabel htmlFor="message">Zpráva</FormLabel>

              <TextareaGroup
                autoComplete="off"
                placeholder="Napište text Vaší zprávy"
                rows={4}
                errors={errors.message}
                {...register('message')}
              />
            </FormControl>

            <FormControl>
              <FormLabel htmlFor="files">Přílohy</FormLabel>
              
              <Button
                as="label"
                variant="outline"
                w='full'
              >
                <input
                  style={{ width: 1, height: 1, padding: 0, margin: 0, opacity: 0.1 }}
                  onChange={(e) => {
                    if (e.target.files) {
                      const newFiles = Array.from(e.target.files)
                      setValue('files', [...filesArray, ...newFiles])
                      // Reset input so name can be selected again if removed
                      e.target.value = ''
                    }
                  }}
                  type="file"
                  multiple
                />
                <Upload color="secondary.500" />
                Nahrát soubor
              </Button>
              {filesArray.length > 0 && (
              <Stack>
                {filesArray.map((file, index) => (
                  <Flex
                    key={`${file.name}-${index}`}
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 4,
                    }}
                    bg='gray.50'
                    p={1.5}
                    border='1px solid'
                    borderColor='gray.100'
                    borderRadius='md'
                  >
                    <Text
                      sx={{
                        maxW: '240px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={`${String(Math.round(file.size / 1024))} kB`}
                    >
                      {file.name}
                    </Text>

                    <IconButton
                      colorScheme="white"
                      aria-label="Odebrat soubor"
                      onClick={() => {
                        const newFiles = [...filesArray]
                        newFiles.splice(index, 1)
                        setValue('files', newFiles.length > 0 ? newFiles : null)
                      }}
                    >
                      <Trash color="red.500" width={4} height={4} />
                    </IconButton>
                  </Flex>
                ))}
              </Stack>
            )}

            </FormControl>

            <Button
              colorScheme="primary"
              type="submit"
              isLoading={isSubmitting}
              isDisabled={isSubmitting}
            >
              Odeslat zprávu
            </Button>
          </Box>
        </form>
      </CardBody>
    </Card>
  )
}
